import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useState } from "react";
import {
  useCompanyInfo,
  useCompanyInfoDelete,
  useCompanyInfoDetail,
  useCompanyInfoSearch,
} from "@/hooks/setting/se_company/useCompanySetting";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import TextAreaBox from "@/components/ui/TextAreaBox";
import { SeCompany } from "@/interface/setting/se_company";
import SettingModal from "@/components/modal/setting/se_company/SettingModal";
import { toast } from "sonner";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";

export default function CompanyInfoPage() {
  const leftColumns: TableColumn[] = [{ key: "companyName", header: "회사명" }];

  const [searchValue, setSearchValue] = useState("");
  const [tablesVer, setTablesVer] = useState(0);
  const [leftTableVer, setLeftTableVer] = useState(0);
  const [selectedLeftRows, setSelectedLeftRows] = useState<number[]>([]);
  const [selectedLeftRow, setSelectedLeftRow] = useState<any>(null);
  const [selectedRightRows, setSelectedRightRows] = useState<number[]>([]);
  const [companyDetail, setCompanyDetail] = useState<SeCompany | undefined>(undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchResults, setSearchResults] = useState<SeCompany[]>([]);

  const {
    data: companyInfoData,
    isLoading,
    error,
  } = useCompanyInfo({
    page: 0,
    size: 20,
    search: "",
    sort: "id,desc",
  });

  const { mutate: companyInfoDetail } = useCompanyInfoDetail(0);

  const { mutate: deleteCompanyInfo } = useCompanyInfoDelete();

  const { mutate: searchCompanyInfo } = useCompanyInfoSearch(searchValue);

  const displayData = searchResults.length > 0 ? searchResults : companyInfoData || [];

  const leftInfiniteScrollPage = useInfiniteScrollPage({
    data: displayData,
    initialLoadCount: 20,
    loadMoreCount: 20,
  });

  const handleReset = () => {
    setSearchValue("");
    setSearchResults([]);
    setLeftTableVer(prev => prev + 1);
    setSelectedLeftRow(null);
    setSelectedLeftRows([]);
    setCompanyDetail(undefined);
  };

  const handleSearch = () => {
    searchCompanyInfo(undefined, {
      onSuccess: (data: any) => {
        const results = Array.isArray(data) ? data : data?.content || [];
        if (results.length > 0) {
          setSearchResults(results);
          setLeftTableVer(prev => prev + 1);
          setSelectedLeftRow(null);
          setSelectedLeftRows([]);
          setCompanyDetail(undefined);
          toast.success("검색이 완료되었습니다.");
        } else {
          toast.warning("검색 결과가 없습니다.");
        }
      },
      onError: error => {
        toast.error("검색에 실패했습니다.");
      },
    });
  };

  // 왼쪽 테이블 선택 핸들러
  const handleLeftRowClick = (row: any) => {
    companyInfoDetail(row.id, {
      onSuccess: (data: SeCompany) => {
        setCompanyDetail(data);
      },
    });
    setSelectedLeftRow(row);
    setSelectedRightRows([]);
  };

  // 상세 정보 입력 핸들러
  const handleDetailChange = (field: keyof SeCompany, value: string) => {
    if (companyDetail) {
      setCompanyDetail({
        ...companyDetail,
        [field]: value,
      });
    }
  };

  const handlerUpdate = () => {
    if (selectedLeftRow) {
      setEditModalOpen(true);
    } else {
      toast.warning("왼쪽 회사를 선택 해주세요.");
      return;
    }
  };

  const handlerDelete = () => {
    if (selectedLeftRow) {
      deleteCompanyInfo(selectedLeftRow.id, {
        onSuccess: () => {
          handleReset();
        },
      });
    } else {
      toast.warning("왼쪽 회사를 선택 해주세요.");
    }
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <InputBox
            label="검색어"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />

          <div className="flex gap-[6px] ml-auto">
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={displayData.length} />
          <div className="flex gap-2">
            <PermissionButton permission="canCreate" variant="etc" onClick={() => setAddModalOpen(true)}>
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc" onClick={handlerUpdate}>
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handlerDelete}>
              삭제
            </PermissionButton>
            <PrintButton
              data={displayData}
              columns={leftColumns}
              title="회사정보"
            />
            <ExcelButton
              data={displayData}
              columns={leftColumns}
              fileName="회사정보"
            />
          </div>
        </div>
        <div key={`tables-v${tablesVer}`} className="flex flex-1 min-h-0 gap-4">
          {/* 왼쪽 영역 */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex-1">
              <Table
                showRowNumber
                key={`${leftInfiniteScrollPage.tableKey}-v${leftTableVer}`}
                {...leftInfiniteScrollPage.tableProps}
                columns={leftColumns}
                data={displayData}
                resizable
                highlightOnClick
                className="h-full"
                selectedRows={selectedLeftRows}
                onRowSelect={setSelectedLeftRows}
                onRowClick={handleLeftRowClick}
              />
            </div>
          </div>

          {/* 오른쪽 영역 */}
          <div className="flex-4 flex flex-col min-h-0 pb-4">
            {/* 헤더 */}
            <div className="bg-[#D0E7AC] rounded-t-[4px] text-center py-6 font-medium text-gray-700">회사 기본 정보</div>
            {/* 컨텐츠 */}
            <div className="border-b border-l border-r border-[#EBEBEB] rounded-bl-[8px] rounded-br-[8px] flex-1 min-h-0 overflow-y-auto">
              <div className="flex flex-col gap-6 px-6 py-4 w-full">
                <div className="flex flex-row gap-6">
                  <InputBox
                    label="회사코드"
                    value={companyDetail?.companyCode || ""}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                  <InputBox
                    label="회사명"
                    value={companyDetail?.companyName || ""}
                    onChange={e => handleDetailChange("companyName", e.target.value)}
                    wrapperClassName="flex-[3]"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <InputBox
                    label="대표자"
                    value={companyDetail?.ceoName || ""}
                    onChange={e => handleDetailChange("ceoName", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                  <InputBox
                    label="사업자또는 법인 등록번호"
                    value={companyDetail?.bizNo || ""}
                    onChange={e => handleDetailChange("bizNo", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                </div>
                <div className="flex flex-row gap-2">
                  <InputBox
                    label="주소"
                    value={companyDetail?.zipcode || ""}
                    onChange={e => handleDetailChange("zipcode", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                  <InputBox
                    label=""
                    value={companyDetail?.address || ""}
                    onChange={e => handleDetailChange("address", e.target.value)}
                    wrapperClassName="flex-[6]"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <InputBox
                    label="업태"
                    value={companyDetail?.bizType || ""}
                    onChange={e => handleDetailChange("bizType", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                  <InputBox
                    label="종목"
                    value={companyDetail?.bizItem || ""}
                    onChange={e => handleDetailChange("bizItem", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                </div>
                <div className="flex flex-row gap-6">
                  <InputBox
                    label="전화번호"
                    value={companyDetail?.tel || ""}
                    onChange={e => handleDetailChange("tel", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                  <InputBox
                    label="휴대전화"
                    value={companyDetail?.mobile || ""}
                    onChange={e => handleDetailChange("mobile", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                  <InputBox
                    label="팩스"
                    value={companyDetail?.fax || ""}
                    onChange={e => handleDetailChange("fax", e.target.value)}
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5] border-none"
                    readOnly
                  />
                </div>
                <div className="flex flex-row">
                  <TextAreaBox
                    label="비고"
                    wrapperClassName="flex-1"
                    className="bg-[#F5F5F5]! border-none"
                    value={companyDetail?.remark || ""}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      handleDetailChange("remark", e.target.value)
                    }
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <SettingModal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} mode="add" />
      <SettingModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        mode="update"
        initialData={companyDetail || undefined}
      />
    </div>
  );
}
